import React from "react";
import { Plus } from "lucide-react";
import { Play } from "lucide-react";

const TestRunsEmptyState = ({
  onCreateTestRun,
  onCreateTestPlan,
  canCreateTestRun = true,
}) => {
  return (
    <div
      className="flex h-full w-full items-start justify-center p-8 flex-wrap md:flex-nowrap"
      style={{ gap: "24px" }}
    >
      {/* 1. Main Empty State Card */}
      <div
        className="flex flex-col items-center text-center bg-[#E3E1DFFF]"
        style={{
          width: "100%",
          maxWidth: "520px",
          minHeight: "450px",
          borderRadius: "12px",
          padding: "48px 32px",
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          border: "1px solid #E5E7EB",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Illustration */}
        <div className="mb-6">
          <div
            className="relative"
            style={{
              width: "160px",
              height: "160px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
            }}
          >
            {/* Clipboard with checkmarks */}
            <div
              className="bg-indigo-50 rounded-lg flex flex-col items-center justify-center relative border border-indigo-100"
              style={{
                width: "80px",
                height: "100px",
                position: "relative",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Clipboard Clip */}
              <div
                className="bg-green-500 rounded-t-sm absolute"
                style={{
                  width: "24px",
                  height: "6px",
                  top: "-3px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  borderRadius: "4px 4px 0 0",
                }}
              />

              {/* Lines/Checks */}
              <div className="flex flex-col gap-2 mt-2 w-full px-3">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></div>
                  <div className="h-1.5 bg-gray-200 rounded w-full"></div>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-indigo-300 flex-shrink-0"></div>
                  <div className="h-1.5 bg-gray-200 rounded w-full"></div>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-indigo-300 flex-shrink-0"></div>
                  <div className="h-1.5 bg-gray-200 rounded w-full"></div>
                </div>
              </div>

              {/* Plus Badge */}
              <div
                className="bg-green-500 rounded-full flex items-center justify-center shadow-lg absolute border-2 border-white"
                style={{
                  width: "28px",
                  height: "28px",
                  bottom: "-8px",
                  left: "-8px",
                }}
              >
                <Plus className="w-4 h-4 text-white" />
              </div>

              {/* Play icon for test run */}
              <div
                className="absolute"
                style={{
                  bottom: "10px",
                  right: "-20px",
                  transform: "rotate(15deg)",
                }}
              >
                <div className="text-indigo-500">
                  <Play className="w-8 h-8" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Heading */}
        <h2
          className="text-gray-900"
          style={{
            fontSize: "30px",
            fontWeight: 700,
            marginBottom: "16px",
            lineHeight: "36px",
          }}
        >
          No test run yet
        </h2>

        {/* Description */}
        <p
          className="text-gray-500"
          style={{
            fontSize: "16px",
            lineHeight: "24px",
            maxWidth: "400px",
            marginBottom: "32px",
            margin: "0 auto 32px",
          }}
        >
          Create your first test run or test plan using the buttons below.
        </p>

        {/* Action Buttons - Side by side */}
        <div className="w-full flex gap-3 justify-center">
          {canCreateTestRun && (
            <button
              onClick={onCreateTestRun}
              className="bg-[#1DD75BFF] hover:bg-[#1AC052FF] disabled:opacity-50 disabled:cursor-not-allowed text-[#0A4D20FF] flex items-center justify-center transition-colors cursor-pointer border-none shadow-sm"
              style={{
                height: "44px",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                gap: "8px",
                padding: "0 16px",
                whiteSpace: "nowrap",
              }}
            >
              <Plus className="w-4 h-4" />
              Add Test Run
            </button>
          )}

          <button
            onClick={onCreateTestPlan}
            className="bg-[#1DD75BFF] hover:bg-[#1AC052FF] disabled:opacity-50 disabled:cursor-not-allowed text-[#0A4D20FF] flex items-center justify-center transition-colors cursor-pointer border-none shadow-sm"
            style={{
              height: "44px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 600,
              gap: "8px",
              padding: "0 16px",
              whiteSpace: "nowrap",
            }}
          >
            <Plus className="w-4 h-4" />
            Add Test Plan
          </button>
        </div>
      </div>

      {/* 2. Info Card (Right Side) */}
      <div
        className="bg-[#E3E1DFFF] hidden md:flex flex-col items-center text-center"
        style={{
          width: "100%",
          maxWidth: "280px",
          minHeight: "450px",
          borderRadius: "12px",
          padding: "48px 32px",
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          border: "1px solid #E5E7EB",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Info content */}
        <div className="mb-6">
          <div
            className="bg-indigo-50 rounded-lg flex items-center justify-center border border-indigo-100"
            style={{
              width: "80px",
              height: "80px",
              margin: "0 auto",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Play className="w-10 h-10 text-indigo-500" />
          </div>
        </div>
        <h3
          className="text-gray-900 text-center w-full"
          style={{
            fontSize: "16px",
            fontWeight: 700,
            marginBottom: "12px",
            lineHeight: "24px",
          }}
        >
          What is a test run?
        </h3>

        <p
          className="text-gray-500 text-center w-full"
          style={{ fontSize: "14px", lineHeight: "20px" }}
        >
          A test run is an executable instance of selected test cases (often
          from a test plan) against a specific build, environment, or
          configuration.
        </p>
      </div>
    </div>
  );
};

export default TestRunsEmptyState;
